import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, Popper, Typography, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import GoogleMapReact, {
  Position,
  ChildComponentProps,
} from 'google-map-react';
import { styles } from '../config/map';

interface MapProps {
  countries: Country[];
  onMapClick: () => void;
  onMarkerClick: (country: string) => void;
}

interface MarkerProps {
  title: string;
  count: number;
  onClick: (country: string) => void;
}

interface StyleProps {
  size: keyof typeof markers;
  hover: boolean;
}

const markers = {
  small: { name: 'marker-s', width: 60, height: 67, bottom: 13, font: 20 },
  medium: { name: 'marker-m', width: 67, height: 76, bottom: 13, font: 22 },
  large: { name: 'marker-l', width: 74, height: 85, bottom: 15, font: 24 },
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  marker: {
    alignItems: 'center',
    backgroundImage: ({ size }) =>
      `url('/outbound/images/${markers[size].name}.png')`,
    backgroundSize: 'contain',
    color: '#FFF',
    display: 'flex',
    filter: ({ hover }) => (hover ? `hue-rotate(180deg)` : `hue-rotate(0deg)`),
    fontSize: ({ size }) => markers[size].font,
    height: ({ size }) => markers[size].height,
    justifyContent: 'center',
    paddingBottom: ({ size }) => markers[size].bottom,
    position: 'absolute',
    textAlign: 'center',
    transform: 'translate(-50%, -60%)',
    width: ({ size }) => markers[size].width,
  },
}));

const Map: React.FC<MapProps> = ({ countries, onMapClick, onMarkerClick }) => {
  const map = useRef<any>();
  const maps = useRef<any>();

  const handleApiLoaded = (api: { map: any; maps: any }) => {
    map.current = api.map;
    maps.current = api.maps;
  };

  useEffect(() => {
    if (maps.current && countries.length > 1) {
      const bounds = new maps.current.LatLngBounds();
      countries.forEach((country) => {
        bounds.extend(
          new maps.current.LatLng(country.latitude, country.longitude)
        );
      });
      map.current.fitBounds(bounds);
    }
  }, [countries]);

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: 'AIzaSyDg9kb5g2oveDzarV50grGQ6oXGd-Tpqtc',
        language: 'en',
      }}
      defaultCenter={{
        lat: 13.7563,
        lng: 100.5018,
      }}
      zoom={3}
      defaultZoom={3}
      options={{
        disableDefaultUI: true,
        minZoom: 3,
        styles,
      }}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={handleApiLoaded}
      onClick={onMapClick}
    >
      {countries.map((item) => (
        <Marker
          key={item.title}
          title={item.title}
          lat={item.latitude}
          lng={item.longitude}
          count={item.count}
          onClick={onMarkerClick}
        />
      ))}
    </GoogleMapReact>
  );
};

const Marker: React.FC<MarkerProps & Position & ChildComponentProps> = ({
  title,
  count,
  onClick,
  $hover,
}) => {
  let size: keyof typeof markers;
  if (count > 10) size = 'large';
  else if (count > 5) size = 'medium';
  else size = 'small';

  const [hover, setHover] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>();
  const { marker } = useStyles({ hover, size });

  useEffect(() => {
    if ($hover) setHover(true);
    else setHover(false);
  }, [$hover]);

  return (
    <>
      <div
        className={marker}
        ref={(r) => (ref.current = r)}
        onClick={() => onClick(title)}
      >
        {count}
      </div>
      <Popper placement="top" open={hover || false} anchorEl={ref.current}>
        <Box p={1} component={Paper}>
          <Typography variant="caption">{title}</Typography>
        </Box>
      </Popper>
    </>
  );
};

export default Map;
